import { useAuth } from "../../authentication/contexts/useAuth";
import React, { useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import findSyncStatuses from "../graphql/queries/findSyncStatuses.graphql";
import triggerSyncStatusesUpdate from "../graphql/mutations/triggerSyncStatusesUpdate.graphql";
import { arrayToObject, removeElement } from "../../common/utils/arrayUtils";
import Table from "../../common/components/elements/Table";
import { CompanySyncStatusCell } from "./CompanySyncStatusCell";
import PropTypes from "prop-types";
import StatelessTable from "../../common/components/elements/StatelessTable";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

CompanySyncStatusesTable.propTypes = {
  allData: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  uuidKey: PropTypes.string.isRequired,
  target: PropTypes.oneOf(["CLIENT", "SITE", "CARRIER"]).isRequired,
  renderMemberCell: PropTypes.func,
  showTrackdechetsInfo: PropTypes.bool.isRequired,
  userHasLinkedAccount: PropTypes.bool,
  ...Table.propTypes,
};

export function CompanySyncStatusesTable({
  allData = [],
  target,
  uuidKey = "uuid",
  renderMemberCell,
  showTrackdechetsInfo,
  userHasLinkedAccount = true,
  ...props
}) {
  const { user } = useAuth();
  const usesTrackdechets = user?.usesTrackdechets;
  const uuids = useMemo(
    () => allData.map((item) => item[uuidKey]),
    [allData, uuidKey],
  );

  const [fetch, { data, loading, error, refetch }] = useLazyQuery(
    findSyncStatuses,
  );

  useEffect(() => {
    if (usesTrackdechets && showTrackdechetsInfo) {
      fetch({ variables: { uuids, target } });
    }
  }, [fetch, showTrackdechetsInfo, target, usesTrackdechets, uuids]);

  const { refreshSyncStatus, isItemRefreshing, hasItemError } =
    useRefreshSyncStatus(target, refetch);

  function getExtraHeaders() {
    if (!showTrackdechetsInfo) {
      return [];
    }

    const statusHeader = "Statut Trackdéchets";
    const memberHeader = renderMemberCell ? (
      <span>
        Vous êtes membre{" "}
        <OverlayTrigger
          trigger={["focus", "hover"]}
          placement="top"
          delay={{ show: 10, hide: 150 }}
          overlay={
            <Tooltip style={{ position: "fixed" }}>
              En tant qu&apos;utilisateur, vous êtes déclaré membre de cette
              entreprise sur Trackdéchets.
            </Tooltip>
          }
        >
          <i className="fa fa-info-circle text-primary" style={{ cursor: "help" }} />
        </OverlayTrigger>{" "}
        {!userHasLinkedAccount && (
          <OverlayTrigger
            trigger={["focus", "hover"]}
            placement="top"
            delay={{ show: 10, hide: 150 }}
            overlay={
              <Tooltip style={{ position: "fixed" }}>
                Vous n&apos;avez pas associé de compte Trackdéchets. Cette
                information ne peut pas être récupérée ou actualisée.
              </Tooltip>
            }
          >
            <i className="fa-solid fa-exclamation-triangle text-warning" style={{ fontSize: "1.2em" }} />
          </OverlayTrigger>
        )}
      </span>
    ) : null;

    return [statusHeader, memberHeader];
  }

  const headers = props.headData.concat(getExtraHeaders());

  const trackdechetsStatuses = useMemo(
    () => arrayToObject(data?.trackdechetsCompaniesSyncStatus || [], "uuid"),
    [data],
  );

  if (!usesTrackdechets) {
    return <StatelessTable {...props} />;
  }

  return (
    <StatelessTable
      {...props}
      headData={headers}
      actions={props.actions}
      rowData={(row, index) => {
        const cells = props.rowData(row, index);

        if (!showTrackdechetsInfo) {
          return cells;
        }

        const uuid = row.uuid;
        const syncStatus = trackdechetsStatuses[uuid] || null;

        return cells.concat([
          <CompanySyncStatusCell
            key="td-sync-status-cell"
            loading={loading || isItemRefreshing(uuid)}
            error={error || hasItemError(uuid)}
            bitFlags={syncStatus?.flags}
            lastUpdatedAt={
              syncStatus?.lastUpdatedAt
                ? new Date(syncStatus.lastUpdatedAt)
                : null
            }
            refresh={() => refreshSyncStatus(uuid)}
          />,
          renderMemberCell ? renderMemberCell(uuid) : null,
        ]);
      }}
    />
  );
}

/**
 * Hook providing logic for refreshing a single status item in the list,
 * by triggering an async update and waiting a bit before fetching the new value.
 */
function useRefreshSyncStatus(target, refetch) {
  const [refreshingRows, setRefreshingRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [triggerUpdate] = useMutation(triggerSyncStatusesUpdate);

  return {
    isItemRefreshing: (uuid) => refreshingRows.includes(uuid),
    hasItemError: (uuid) => errors.includes(uuid),
    refreshSyncStatus: async (uuid) => {
      setRefreshingRows(refreshingRows.concat(uuid));
      setErrors(removeElement(errors, uuid));

      const options = {
        variables: {
          uuids: [uuid],
          target,
        },
      };

      try {
        // trigger async update of the status
        await triggerUpdate(options);

        // wait a second
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // attempt to fetch the updated result,
        // which will update Apollo's cached data for this item:
        await refetch({
          ...options,
          fetchPolicy: "network-only",
        });
      } catch {
        setErrors(errors.concat(uuid));
      } finally {
        setRefreshingRows(removeElement(refreshingRows, uuid));
      }
    },
  };
}
